import { List, MagnifyingGlass } from "@phosphor-icons/react"
import { useQuery } from "@tanstack/react-query"
import { Button } from "@tokenterminal/core/ui/button/Button"
import { IconButton } from "@tokenterminal/core/ui/button/IconButton"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { KeyboardShortcut } from "@tokenterminal/core/ui/keyboard/KeyboardShortcut"
import { Badge } from "@tokenterminal/core/ui/messaging/badge/Badge"
import { ModalCloseButton } from "@tokenterminal/core/ui/overlays/modal/Header"
import { Logo } from "@tokenterminal/ui/Logo"
import { ClientOnlySuspense } from "components/ClientOnlySuspense"
import { useSearchActions } from "components/terminal/Navigation/Search/useSearch"
import { apiGET } from "data/api/get"
import { useIsMac } from "hooks/useIsMac"
import { FeatureSwitch } from "modules/featureswitch/FeatureSwitch"
import NextLink from "next/link"
import { useRouter } from "next/router"
import { type ReactNode, useMemo, useState } from "react"
import { ModalOverlay, Modal as AriaModal, Dialog } from "react-aria-components"
import { twMerge } from "tailwind-merge"
import { SearchProvider } from "../../terminal/Navigation/Search/SearchProvider"
import { AccountDropdown } from "./AccountDropdown/AccountDropdown"
import AccountDropdownSkeleton from "./AccountDropdown/AccountDropdownSkeleton"
import styles from "./Header.module.css"

const focusStyles =
  "focus-visible:outline-none focus-visible:outline-2 focus-visible:outline-sky-500 focus-visible:outline-offset-[3px] rounded"

const NavLink = ({
  children,
  className,
  href,
  onClick,
  isActive: makeIsActive,
}: {
  children: ReactNode
  className?: string
  href: string
  onClick?: () => void
  isActive?: (pathname: string) => boolean
}) => {
  const router = useRouter()
  const isActive = useMemo(() => {
    if (makeIsActive) {
      return makeIsActive(router.pathname)
    }
    return router.pathname.startsWith(href)
  }, [href, makeIsActive, router.pathname])

  return (
    <NextLink
      className={twMerge(
        `text-sm whitespace-nowrap text-fg-default`,
        isActive
          ? "text-fg-default hover:opacity-90"
          : "text-fg-secondary hover:text-neutral-600 dark:hover:text-neutral-300",
        focusStyles,
        className
      )}
      href={href}
      onClick={onClick}
    >
      {children}
    </NextLink>
  )
}

export function Header({
  disableGlobalSearchOverlay = false,
}: {
  disableGlobalSearchOverlay?: boolean
}) {
  const { openSearch } = useSearchActions()
  const [isOpen, setIsOpen] = useState(false)
  const isMacOS = useIsMac()
  const router = useRouter()

  const { data: dashboards = [], isLoading: isDashboardsLoading } = useQuery({
    queryKey: ["dashboards"],
    queryFn: async () => {
      const { data } = await apiGET("/v1/dashboards")
      return data
    },
  })

  const handleNavigate = () => {
    setIsOpen(false)
  }

  const isDiscoverDashboardPage = useMemo(() => {
    const id = router.query.id

    if (id === undefined) {
      return undefined
    }

    if (
      router.pathname !== "/studio/dashboards/[id]" ||
      dashboards.length === 0
    ) {
      return false
    }

    return dashboards.some((dashboard) => dashboard.user_dashboard_id === id)
  }, [dashboards, router.pathname, router.query.id])

  return (
    <>
      <SearchProvider disableGlobalOverlay={disableGlobalSearchOverlay} />
      <header className="flex justify-between items-center gap-3 w-full px-2.5 md:px-2.5 py-1 h-[56px] flex-shrink-0">
        <div className="flex items-center gap-4 w-[40px] md:w-[35%]">
          <NextLink
            href="/"
            className={twMerge(focusStyles, "hidden lg:block")}
          >
            <Logo width={120} />
          </NextLink>
          {/* Skip to content button */}
          <a
            href="#main-content"
            className={twMerge(
              "hidden pointer-events-none lg:flex opacity-0 focus:opacity-100 focus:pointer-events-auto items-center",
              "absolute top-3.5 left-2.5 z-50 bg-bg-default px-3 py-1 text-sm font-medium",
              focusStyles
            )}
          >
            Skip to content
          </a>
          <Button
            variant="ghost"
            className="flex items-center gap-3 lg:hidden !px-2 py-2 hover:bg-neutral-950/5 dark:hover:bg-neutral-50/5 rounded-lg"
            onPress={() => setIsOpen(true)}
          >
            <Icon as={List} />
            <Logo width={120} />
          </Button>
          <div className="hidden lg:flex gap-4 items-center">
            <NavLink href="/" isActive={(pathname) => pathname === "/"}>
              Home
            </NavLink>
            <NavLink
              href="/discover"
              isActive={(pathname) =>
                pathname === "/discover" || isDiscoverDashboardPage === true
              }
            >
              Discover
            </NavLink>
            <NavLink href="/markets">Market sectors</NavLink>
            <NavLink href="/projects">Projects</NavLink>
            <NavLink href="/metrics">
              Metrics
              <Badge
                size="xsmall"
                color="cyan"
                variant="muted"
                className="leading-normal ml-1"
              >
                New
              </Badge>
            </NavLink>
            <NavLink href="/datasets">Datasets</NavLink>
            <NavLink
              href="/studio/dashboards"
              isActive={(pathname) => {
                return (
                  !isDashboardsLoading &&
                  pathname.startsWith("/studio") &&
                  isDiscoverDashboardPage === false
                )
              }}
            >
              Studio
            </NavLink>
          </div>
        </div>
        <div className="flex items-center gap-1 lg:gap-3 w-[60px] lg:w-[35%] justify-end">
          <Button
            className="text-fg-secondary w-[200px] justify-between !px-3 hidden lg:flex"
            variant="muted"
            onPress={openSearch}
          >
            <div className="flex items-center gap-2">
              <Icon as={MagnifyingGlass} />
              <span className="font-normal">Search</span>
            </div>
            <KeyboardShortcut className="flex items-center gap-1 p-1.5 !h-[20px] text-xs !-mr-1">
              {isMacOS ? (
                <div className="text-fg-secondary">
                  {KeyboardShortcut.CommandSymbol}
                </div>
              ) : (
                <>
                  <div className="text-fg-secondary">Ctrl</div>
                  <div className="text-fg-secondary">+</div>
                </>
              )}
              <div className="text-fg-secondary">K</div>
            </KeyboardShortcut>
          </Button>
          <IconButton
            variant="ghost"
            icon={MagnifyingGlass}
            onPress={openSearch}
            label="Search"
            className="flex-shrink-0 flex lg:hidden"
          />
          <FeatureSwitch feature="ACCOUNT_PAGE_ENABLED">
            <ClientOnlySuspense fallback={<AccountDropdownSkeleton />}>
              <AccountDropdown />
            </ClientOnlySuspense>
          </FeatureSwitch>
        </div>
      </header>

      <ModalOverlay
        className="tt-modal-overlay items-stretch justify-start"
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        isDismissable
      >
        <AriaModal
          className={`${styles.myModal} tt-modal !rounded-tl-none !rounded-bl-none`}
        >
          <Dialog
            className="tt-modal-dialog flex flex-col !max-w-[60vw] !h-auto !max-h-[var(--visual-viewport-height)]"
            data-size="full"
          >
            <div className="p-6 flex flex-col gap-5">
              <div className="flex items-center gap-3 justify-between">
                <Logo width={120} />
                <ModalCloseButton />
              </div>
              <NavLink
                className="text-base"
                href="/"
                onClick={handleNavigate}
                isActive={(pathname) => pathname === "/"}
              >
                Home
              </NavLink>
              <NavLink
                className="text-base"
                href="/discover"
                onClick={handleNavigate}
              >
                Discover
              </NavLink>
              <NavLink
                className="text-base flex items-center gap-1"
                href="/markets"
                onClick={handleNavigate}
              >
                Market sectors
              </NavLink>
              <NavLink
                className="text-base"
                href="/projects"
                onClick={handleNavigate}
              >
                Projects
              </NavLink>
              <NavLink
                className="text-base flex items-center gap-1"
                href="/metrics"
                onClick={handleNavigate}
              >
                Metrics
                <Badge
                  size="xsmall"
                  color="cyan"
                  variant="muted"
                  className="leading-normal"
                >
                  New
                </Badge>
              </NavLink>
              <NavLink
                className="text-base"
                href="/datasets"
                onClick={handleNavigate}
              >
                Datasets
              </NavLink>
              <NavLink
                className="text-base"
                href="/studio/dashboards"
                onClick={handleNavigate}
                isActive={(pathname) => pathname.startsWith("/studio")}
              >
                Studio
              </NavLink>
            </div>
          </Dialog>
        </AriaModal>
      </ModalOverlay>
    </>
  )
}
